import type {ReactElement, RefObject} from 'react';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import type {CollisionDetection, DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import {closestCenter, DndContext, DragOverlay, MouseSensor, rectIntersection, TouchSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {LayerRow} from './components/layer-row/layer-row';
import {LayerGroupRow} from './components/layer-group-row/layer-group-row';
import type {LayerEntry, LayerGroupEntry} from './layers-panel.types';
import {LayerType, SELECTED_ROW_DATA_ATTRIBUTE, SelectionIntent} from './layers-panel.types';
import {
  applySelection,
  commitDragEnd,
  computeRangeUids,
  findGroupEntry,
  getCurrentPage,
  getDraggedLayer,
  getEntryUids,
  getSelectionIntent,
  getSortableId,
  getVisibleEntriesDuringDrag,
  isOuterRowPresent,
  toGroupSortableId,
} from './layers-panel.helper';
import {useAnimatedPanelHeight, useLockLayoutDuringDrag} from './layers-panel.hooks';
import {selectLayerPanelRows} from '@Components/poster-editor/poster-editor-reducer';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {DRILL_IN_EVENT_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {useAppSelector} from '@/hooks';
import type {RootState} from '@/store';
import styles from './layers-panel.module.scss';

interface LayersPanelProps {
  onClose: () => void;
  isOpenedInModal?: boolean;
  bodyRef?: RefObject<HTMLDivElement | null>;
}

const DRAG_DISTANCE_PX = 8;
const TOUCH_LONG_PRESS_DELAY_MS = 250;
const TOUCH_LONG_PRESS_TOLERANCE_PX = 5;
const DRAG_OVERLAY_Z_INDEX_ABOVE_POPUPS = 10100;
const EMPTY_IDS: readonly string[] = [];
const DRAG_MODIFIERS = [restrictToVerticalAxis];

export function LayersPanel({onClose, isOpenedInModal = false, bodyRef}: LayersPanelProps): ReactElement {
  // Mobile multi-select: rows show selection checkboxes since cmd/shift clicks aren't available on touch.
  const showSelectionCheckboxes = isEditorMobileVariant();
  const entries = useAppSelector(selectLayerPanelRows);
  const activeIds = useAppSelector((state: RootState): readonly string[] => {
    const currentPageId = state.posterEditor.currentPageId;
    return state.posterEditor.activeItemIds[currentPageId] ?? EMPTY_IDS;
  });
  const activeIdsSet = useMemo(() => new Set(activeIds), [activeIds]);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [draggedIds, setDraggedIds] = useState<readonly string[]>(EMPTY_IDS);
  const isMultiDrag = draggedIds.length > 1;
  const visibleEntries = useMemo(
    () => (activeDragId === null ? entries : getVisibleEntriesDuringDrag(entries, activeDragId, draggedIds)),
    [entries, activeDragId, draggedIds]
  );
  const outerIds = useMemo(() => visibleEntries.map(getSortableId), [visibleEntries]);
  const memberToGroupId = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of entries) {
      if (entry.layerType !== LayerType.Group) {
        continue;
      }
      for (const uid of entry.memberUids) {
        map.set(uid, entry.groupId);
      }
    }
    return map;
  }, [entries]);

  const anchorIdRef = useRef<string | null>(null);
  const previousShiftRangeRef = useRef<string[]>([]);

  // A plain/cmd click or drill-in commits the selection: the clicked row becomes the anchor and the
  // next shift range starts fresh, so re-adjusting a later range can deselect what that range added.
  const commitAnchor = (sortableId: string): void => {
    anchorIdRef.current = sortableId;
    previousShiftRangeRef.current = [];
  };

  // Cmd/Ctrl click: add the unit if any part is unselected, otherwise remove it.
  const toggleSelection = (page: Page, uids: readonly string[]): void => {
    if (uids.length === 0) {
      return;
    }
    const uidSet = new Set(uids);
    const areAllSelected = uids.every((uid) => activeIdsSet.has(uid));
    const selectionWithoutTargets = activeIds.filter((uid) => !uidSet.has(uid));
    applySelection(page, areAllSelected ? selectionWithoutTargets : [...selectionWithoutTargets, ...uids]);
  };

  // Shift click: selection = committed base ∪ range(anchor → clicked). The committed base is the live
  // selection with the previous shift range stripped out, so shrinking or redirecting the range deselects
  // the rows it no longer covers while keeping everything locked in by plain/cmd clicks.
  const selectRange = (page: Page, targetSortableId: string): void => {
    const previousAnchorId = anchorIdRef.current;
    const anchorId = previousAnchorId !== null && isOuterRowPresent(entries, previousAnchorId) ? previousAnchorId : targetSortableId;
    anchorIdRef.current = anchorId;
    const rangeUids = computeRangeUids(entries, anchorId, targetSortableId);
    const previousShiftRange = new Set(previousShiftRangeRef.current);
    const committedBase = activeIds.filter((uid) => !previousShiftRange.has(uid));
    previousShiftRangeRef.current = rangeUids;
    applySelection(page, [...new Set([...committedBase, ...rangeUids])]);
  };

  const applySelectionIntent = (sortableId: string, uids: readonly string[], event: React.MouseEvent<HTMLDivElement>): void => {
    const page = getCurrentPage();
    if (!page) {
      return;
    }
    const intent = getSelectionIntent(event);
    switch (intent) {
      case SelectionIntent.Replace:
        applySelection(page, uids);
        commitAnchor(sortableId);
        break;
      case SelectionIntent.Toggle:
        toggleSelection(page, uids);
        commitAnchor(sortableId);
        break;
      case SelectionIntent.Range:
        selectRange(page, sortableId);
        break;
      default:
        throw new Error(`Unhandled selection intent: ${String(intent satisfies never)}`);
    }
  };

  // Selecting a single member isolates it inside its group (the one partial-group state the canvas allows),
  // routed through the canvas drill-in event so group propagation lets the single selection stand.
  const selectGroupMember = (uid: string): void => {
    const page = getCurrentPage();
    if (!page) {
      return;
    }
    applySelection(page, [uid], {selectionEvent: new MouseEvent(DRILL_IN_EVENT_TYPE)});
    const groupId = memberToGroupId.get(uid);
    if (groupId !== undefined) {
      commitAnchor(toGroupSortableId(groupId));
    }
  };

  const handleItemClick = (uid: string, event: React.MouseEvent<HTMLDivElement>): void => {
    if (memberToGroupId.has(uid)) {
      selectGroupMember(uid);
      return;
    }
    applySelectionIntent(uid, [uid], event);
  };

  const handleGroupClick = (groupId: string, event: React.MouseEvent<HTMLDivElement>): void => {
    const groupEntry = findGroupEntry(entries, groupId);
    if (!groupEntry) {
      return;
    }
    applySelectionIntent(toGroupSortableId(groupId), groupEntry.memberUids, event);
  };

  // A checkbox tap is the touch equivalent of a cmd/ctrl click: toggle the unit in and out of the selection.
  const handleCheckboxToggle = (sortableId: string, uids: readonly string[]): void => {
    const page = getCurrentPage();
    if (!page) {
      return;
    }
    toggleSelection(page, uids);
    commitAnchor(sortableId);
  };

  const handleItemCheckboxClick = (uid: string): void => {
    handleCheckboxToggle(uid, [uid]);
  };

  const handleGroupCheckboxClick = (groupId: string): void => {
    const groupEntry = findGroupEntry(entries, groupId);
    if (!groupEntry) {
      return;
    }
    handleCheckboxToggle(toGroupSortableId(groupId), groupEntry.memberUids);
  };

  const {listRef, getScrollViewport, measuring, lockedBodyStyle, lockLayout, releaseLayout} = useLockLayoutDuringDrag(activeDragId);
  const panelRef = useRef<HTMLDivElement>(null);
  const canAnimatePanelHeight = !isOpenedInModal && activeDragId === null && entries.length > 0;
  const panelHeightStyle = useAnimatedPanelHeight(panelRef, listRef, canAnimatePanelHeight);

  const mouseSensor = useSensor(MouseSensor, {activationConstraint: {distance: DRAG_DISTANCE_PX}});
  const touchSensor = useSensor(TouchSensor, {activationConstraint: {delay: TOUCH_LONG_PRESS_DELAY_MS, tolerance: TOUCH_LONG_PRESS_TOLERANCE_PX}});
  const sensors = useSensors(mouseSensor, touchSensor);

  useEffect((): (() => void) => {
    // scroll the selected row into view when the panel is mounted
    const frameId = requestAnimationFrame((): void => {
      listRef.current?.querySelector<HTMLElement>(`[${SELECTED_ROW_DATA_ATTRIBUTE}]`)?.scrollIntoView({block: 'center'});
    });
    return (): void => {
      cancelAnimationFrame(frameId);
    };
  }, []);

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const collisions = rectIntersection(args);
    if (collisions.length > 0) {
      return collisions;
    }
    const bodyRect = getScrollViewport()?.getBoundingClientRect();
    const pointer = args.pointerCoordinates;
    if (!bodyRect || !pointer) {
      return collisions;
    }
    // pointer is over the empty space the collapse left in the height-locked body: snap to the closest row so the drop lands at the end
    const isPointerInsideBody = pointer.x >= bodyRect.left && pointer.x <= bodyRect.right && pointer.y >= bodyRect.top && pointer.y <= bodyRect.bottom;
    return isPointerInsideBody ? closestCenter(args) : collisions;
  }, [getScrollViewport]);

  const isGroupFullySelected = (entry: LayerGroupEntry): boolean => {
    return entry.memberUids.length > 0 && entry.memberUids.every((uid) => activeIdsSet.has(uid));
  };

  const isEntryFullySelected = (entry: LayerEntry): boolean => {
    return entry.layerType === LayerType.Item ? activeIdsSet.has(entry.uid) : isGroupFullySelected(entry);
  };

  // Canva-style drag/selection coupling: dragging an unselected row first makes it the sole selection.
  const selectDraggedRowIfNeeded = (activeId: string): void => {
    const entry = entries.find((e) => getSortableId(e) === activeId);
    if (!entry) {
      if (!activeIdsSet.has(activeId)) {
        selectGroupMember(activeId);
      }
      return;
    }
    const unitUids = getEntryUids(entry);
    if (unitUids.every((uid) => activeIdsSet.has(uid))) {
      return;
    }
    const page = getCurrentPage();
    if (!page) {
      return;
    }
    applySelection(page, unitUids);
    commitAnchor(activeId);
  };

  // Dragging a row that was already fully selected carries every selected outer row along as one block; any other row drags solo.
  const getDraggedSortableIds = (activeId: string): string[] => {
    const entry = entries.find((e) => getSortableId(e) === activeId);
    if (!entry || !getEntryUids(entry).every((uid) => activeIdsSet.has(uid))) {
      return [activeId];
    }
    return entries.filter(isEntryFullySelected).map(getSortableId);
  };

  const onDragStart = (event: DragStartEvent): void => {
    lockLayout();
    const activeId = String(event.active.id);
    setActiveDragId(activeId);
    setDraggedIds(getDraggedSortableIds(activeId));
    selectDraggedRowIfNeeded(activeId);
  };

  const resetDragState = useCallback((): void => {
    setActiveDragId(null);
    setDraggedIds(EMPTY_IDS);
    releaseLayout();
  }, [releaseLayout]);

  const onDragEnd = useCallback(
    (event: DragEndEvent): void => {
      resetDragState();
      if (commitDragEnd(event, entries, draggedIds)) {
        trackPosterBuilderGA4Events(GA4EventName.LAYER_ARRANGED);
      }
    },
    [entries, draggedIds, resetDragState]
  );

  const draggedLayer = useMemo(() => (activeDragId ? getDraggedLayer(activeDragId, entries) : null), [activeDragId, entries]);

  const isGroupExpanded = (entry: LayerGroupEntry): boolean => {
    // while dragging, only the group containing the dragged member stays expanded
    if (activeDragId !== null) {
      return memberToGroupId.get(activeDragId) === entry.groupId;
    }
    return entry.memberUids.some((uid) => activeIdsSet.has(uid));
  };

  const renderGroupMembers = (entry: LayerGroupEntry, isExpanded: boolean): ReactElement | null => {
    if (!isExpanded) {
      return null;
    }
    return (
      <SortableContext items={entry.memberUids} strategy={verticalListSortingStrategy}>
        <div className={`${styles.groupMembers} flex-v-row gap-2`}>
          {entry.memberUids.map((uid) => (
            <LayerRow key={uid} uid={uid} isSelected={activeIdsSet.has(uid)} isInsideGroup onClick={handleItemClick} />
          ))}
        </div>
      </SortableContext>
    );
  };

  const renderEntry = (entry: LayerEntry): ReactElement => {
    if (entry.layerType === LayerType.Item) {
      return (
        <LayerRow
          key={entry.uid}
          uid={entry.uid}
          isSelected={activeIdsSet.has(entry.uid)}
          showSelectionCheckbox={showSelectionCheckboxes}
          onClick={handleItemClick}
          onCheckboxClick={handleItemCheckboxClick}
        />
      );
    }
    const isGroupSelected = isGroupFullySelected(entry);
    const isExpanded = isGroupExpanded(entry);
    return (
      <React.Fragment key={entry.groupId}>
        <LayerGroupRow
          groupId={entry.groupId}
          isExpanded={isExpanded}
          isSelected={isGroupSelected}
          showSelectionCheckbox={showSelectionCheckboxes}
          onClick={handleGroupClick}
          onCheckboxClick={handleGroupCheckboxClick}
        />
        {renderGroupMembers(entry, isExpanded)}
      </React.Fragment>
    );
  };

  // One shell peeking out bottom-right behind the dragged row regardless of selection size.
  const renderOverlayStackShell = (): ReactElement | null => {
    if (!isMultiDrag) {
      return null;
    }
    return <div className={styles.overlayStackShell} />;
  };

  // The badge counts canvas items (group rows contribute every member), matching the layers modal's Select button.
  const renderOverlayCountBadge = (): ReactElement | null => {
    if (!isMultiDrag) {
      return null;
    }
    const draggedIdSet = new Set(draggedIds);
    const draggedItemCount = entries.reduce((count, entry) => (draggedIdSet.has(getSortableId(entry)) ? count + getEntryUids(entry).length : count), 0);
    return <div className={`${styles.overlayCountBadge} flex-center`}>{draggedItemCount}</div>;
  };

  const renderOverlay = (): ReactElement | null => {
    if (!draggedLayer) {
      return null;
    }
    const row =
      draggedLayer.layerType === LayerType.Group ? (
        <LayerGroupRow
          groupId={draggedLayer.entry.groupId}
          isExpanded={false}
          isSelected={isGroupFullySelected(draggedLayer.entry)}
          isOverlay
          hideStackBars={isMultiDrag}
          showSelectionCheckbox={showSelectionCheckboxes}
        />
      ) : (
        <LayerRow uid={draggedLayer.uid} isSelected={activeIdsSet.has(draggedLayer.uid)} isOverlay showSelectionCheckbox={showSelectionCheckboxes && !memberToGroupId.has(draggedLayer.uid)} />
      );
    return (
      <div className={`${styles.overlayCard} _position-relative flex-v-row gap-2 _full-width`}>
        {renderOverlayStackShell()}
        <div className={`${styles.overlayFrontRow} ${isMultiDrag ? styles.overlayFrontRowChrome : ''} flex-v-row`}>{row}</div>
        {renderOverlayCountBadge()}
      </div>
    );
  };

  const panelClasses = `${styles.panel} flex-v-row _full-width _overflow-hidden ${isOpenedInModal ? styles.inModal : ''} ${panelHeightStyle ? styles.panelHeightAnimated : ''}`;

  const renderHeader = (): ReactElement | null => {
    if (isOpenedInModal) {
      return null;
    }
    return (
      <div className={`${styles.header} flexbox flex-items-center flex-justify-between flex-shrink-0 spacing-m-b-2`}>
        <Text val={window.i18next.t('pmwjs_select_layers')} size={TextSize.MD} bold />
        <ClickableDiv onClick={onClose} className={`${styles.closeButton} flex-center _cursor-pointer`} aria-label={window.i18next.t('pmwjs_close')}>
          <Icon icon="icon-close" size={IconSize.SIZE_ICON_16} />
        </ClickableDiv>
      </div>
    );
  };

  const renderEmptyState = (): ReactElement => {
    return (
      <div className={`${styles.emptyState} flex-v-row flex-align-items-center gap-4`}>
        <div className={`${styles.emptyStateIcon} flex-center`}>
          <Icon icon="icon-ban" size={IconSize.SIZE_ICON_24} />
        </div>
        <Text size={TextSize.SM} val={window.i18next.t('pmwjs_layers_panel_empty')} />
      </div>
    );
  };

  const renderBody = (): ReactElement => {
    if (entries.length === 0) {
      return renderEmptyState();
    }
    return (
      <DndContext sensors={sensors} modifiers={DRAG_MODIFIERS} collisionDetection={collisionDetection} measuring={measuring} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={resetDragState}>
        <SortableContext items={outerIds} strategy={verticalListSortingStrategy}>
          <div ref={listRef} className="flex-v-row gap-2">
            {visibleEntries.map(renderEntry)}
          </div>
        </SortableContext>
        {createPortal(
          <DragOverlay dropAnimation={null} zIndex={DRAG_OVERLAY_Z_INDEX_ABOVE_POPUPS}>
            {renderOverlay()}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    );
  };

  return (
    <div ref={panelRef} className={panelClasses} style={panelHeightStyle}>
      {renderHeader()}
      <div ref={bodyRef} className={`${styles.body} flex-v-row flex-1`} style={lockedBodyStyle}>
        {renderBody()}
      </div>
    </div>
  );
}

